import { z } from 'zod';
import { basisPoints, countryCode, currency, email, id, isoDate, moneyMinor, nonEmpty, optionalText, phone, quantity } from './common';

// ---------- Auth ----------
export const loginRequest = z.object({
  email,
  password: z.string().min(1).max(200),
  tenantSlug: z.string().trim().toLowerCase().max(40).optional(),
});
export const selectTenantRequest = z.object({ tenantId: id });
export const passwordSchema = z
  .string()
  .min(10, 'Use at least 10 characters')
  .max(200)
  .refine((p) => /[A-Za-z]/.test(p) && /[0-9]/.test(p), 'Use letters and numbers');
export const passwordResetRequest = z.object({ email });
export const passwordResetConfirm = z.object({ token: z.string().min(20).max(200), newPassword: passwordSchema });
export const acceptInvitation = z.object({ token: z.string().min(20).max(200), fullName: nonEmpty(120).optional(), password: passwordSchema });
export const changePasswordRequest = z.object({ currentPassword: z.string().min(1).max(200), newPassword: passwordSchema });

// ---------- Platform ----------
export const createTenantRequest = z.object({
  slug: z.string().trim().toLowerCase().regex(/^[a-z0-9]{3,40}$/, '3–40 lowercase letters or digits'),
  legalName: nonEmpty(200),
  displayName: nonEmpty(120),
  countryCode,
  defaultCurrency: currency.optional(),
  timezone: z.string().max(60).optional(),
  planCode: z.string().max(40).default('starter'),
  admin: z.object({ email, fullName: nonEmpty(120) }),
});
export const updateTenantStatusRequest = z.object({ status: z.enum(['active', 'suspended', 'closed']), reason: nonEmpty(500) });
export const tenantDomainRequest = z.object({
  hostname: z.string().trim().toLowerCase().regex(/^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,}$/, 'Invalid hostname'),
  kind: z.enum(['platform_subdomain', 'custom']),
  isPrimary: z.boolean().default(false),
});
export const updateDomainStatusRequest = z.object({ status: z.enum(['pending', 'active', 'disabled']) });
export const planRequest = z.object({
  code: z.string().regex(/^[a-z0-9_-]{2,40}$/),
  name: nonEmpty(80),
  priceMinor: moneyMinor,
  currency: currency.default('INR'),
  billingPeriod: z.enum(['monthly', 'yearly']).default('monthly'),
  maxUsers: z.number().int().positive().nullable(),
  maxStorageBytes: z.number().int().positive().nullable(),
  maxOrdersPerMonth: z.number().int().positive().nullable(),
  maxEmailsPerMonth: z.number().int().positive().nullable(),
  maxMessagesPerMonth: z.number().int().positive().nullable(),
  maxApiRequestsPerDay: z.number().int().positive().nullable(),
  modules: z.array(z.string().max(40)).max(40).default([]),
  overageBehaviour: z.enum(['block', 'warn']).default('block'),
  warnThresholdBp: basisPoints.default(8000),
  active: z.boolean().default(true),
  priceNote: optionalText(500),
});
export const changeSubscriptionRequest = z.object({
  planCode: z.string().max(40),
  status: z.enum(['trialing', 'active', 'past_due', 'suspended', 'cancelled']).optional(),
  overrides: z
    .object({
      maxUsers: z.number().int().positive().nullable().optional(),
      maxStorageBytes: z.number().int().positive().nullable().optional(),
      maxOrdersPerMonth: z.number().int().positive().nullable().optional(),
      maxEmailsPerMonth: z.number().int().positive().nullable().optional(),
    })
    .optional(),
});
export const countryRequest = z.object({
  code: countryCode,
  name: nonEmpty(80),
  defaultCurrency: currency,
  defaultTimezone: z.string().max(60),
  fiscalYearStartMonth: z.number().int().min(1).max(12).default(1),
  active: z.boolean().default(true),
});
export const identifierTypeRequest = z.object({
  code: z.string().regex(/^[A-Z0-9_]{2,40}$/),
  countryCode: countryCode.nullable(),
  name: nonEmpty(120),
  kind: z.enum(['tax', 'business_registration', 'licence', 'identity', 'other']),
  validationRegex: z.string().max(300).nullable().optional(),
  isSensitive: z.boolean().default(false),
  maskVisibleSuffix: z.number().int().min(0).max(10).default(4),
  notes: optionalText(1000),
  active: z.boolean().default(true),
});
export const documentTypeRequest = z.object({
  code: z.string().regex(/^[A-Z0-9_]{2,60}$/),
  countryCode: countryCode.nullable(),
  name: nonEmpty(120),
  category: z.enum(['business_registration', 'tax_registration', 'identity', 'licence', 'manufacturing_licence', 'quality_certification', 'product_certificate', 'other']),
  isSensitive: z.boolean().default(false),
  hasExpiry: z.boolean().default(false),
  expiryWarningDays: z.number().int().min(0).max(365).default(30),
  allowedContentTypes: z.array(z.string().max(100)).min(1).max(10).default(['application/pdf', 'image/jpeg', 'image/png']),
  maxSizeBytes: z.number().int().min(1024).max(25 * 1024 * 1024).default(10 * 1024 * 1024),
  retentionDays: z.number().int().positive().nullable().optional(),
  active: z.boolean().default(true),
});
export const requirementSetRequest = z.object({
  countryCode,
  effectiveFrom: isoDate,
  notes: optionalText(2000),
  requirements: z
    .array(
      z
        .object({
          appliesTo: z.enum(['tenant', 'customer', 'manufacturer', 'supplier', 'product']),
          partyCategory: z.string().max(40).nullable().optional(),
          documentTypeCode: z.string().max(60).nullable().optional(),
          identifierTypeCode: z.string().max(40).nullable().optional(),
          requirement: z.enum(['mandatory', 'optional', 'conditional']),
          conditionNote: optionalText(500),
          blocksOrderSubmission: z.boolean().default(false),
        })
        .refine((r) => r.documentTypeCode || r.identifierTypeCode, 'A requirement needs a document type or identifier type'),
    )
    .max(200),
});
export const taxCategoryRequest = z.object({
  countryCode,
  code: z.string().regex(/^[A-Z0-9_.-]{1,40}$/),
  name: nonEmpty(120),
  rateBp: basisPoints,
  components: z.array(z.object({ code: z.string().max(20), name: z.string().max(60), rateBp: basisPoints, appliesWhen: z.enum(['always', 'intra_region', 'inter_region']).optional() })).max(8).default([]),
  effectiveFrom: isoDate,
  effectiveTo: isoDate.nullable().optional(),
});

// ---------- Tenant administration ----------
/** A bank account printed in the "Bank details" block of issued documents. Not a secret: it exists to be shown to customers. */
export const bankAccountRequest = z.object({
  label: optionalText(60),
  accountName: nonEmpty(120),
  bankName: nonEmpty(120),
  accountNumber: z.string().trim().min(4).max(40).regex(/^[A-Za-z0-9 -]+$/),
  ifsc: optionalText(20),
  branch: optionalText(120),
  upiId: optionalText(120),
  showOnDocuments: z.boolean().default(true),
  displayOrder: z.number().int().min(0).max(999).default(0),
});

export const updateCompanyProfileRequest = z.object({
  legalName: nonEmpty(200),
  displayName: nonEmpty(120),
  regionCode: z.string().max(10).nullable().optional(),
  timezone: z.string().max(60),
  defaultCurrency: currency,
  addressLine1: optionalText(200),
  addressLine2: optionalText(200),
  city: optionalText(100),
  postalCode: optionalText(20),
  registeredAddress: optionalText(500),
  contactEmail: email.nullable().optional(),
  contactPhone: phone.nullable().optional(),
  website: z.string().max(200).nullable().optional(),
  invoiceFooter: optionalText(1000),
  poFooter: optionalText(1000),
  version: z.number().int().positive(),
});
export const tenantIdentifierRequest = z.object({
  identifierTypeCode: z.string().max(40),
  value: nonEmpty(80),
  regionCode: z.string().max(10).nullable().optional(),
});
export const inviteUserRequest = z.object({ email, fullName: nonEmpty(120), roleCodes: z.array(z.string().max(40)).min(1).max(10) });
export const setUserRolesRequest = z.object({ roleCodes: z.array(z.string().max(40)).min(1).max(10) });
export const setMembershipStatusRequest = z.object({ status: z.enum(['active', 'suspended']) });
export const roleRequest = z.object({
  code: z.string().regex(/^[a-z0-9_]{2,40}$/),
  name: nonEmpty(80),
  description: optionalText(300),
  permissions: z.array(z.string().max(60)).max(80),
});
export const documentSequenceRequest = z.object({
  docType: z.enum(['sales_order', 'purchase_order', 'proforma_invoice', 'goods_receipt', 'shipment', 'payment']),
  prefix: z.string().max(20).regex(/^[A-Za-z0-9/_-]*$/),
  padding: z.number().int().min(3).max(10),
  resetPolicy: z.enum(['never', 'calendar_year', 'fiscal_year']),
});
export const paymentTermsRequest = z.object({
  code: z.string().regex(/^[A-Z0-9_]{2,30}$/),
  name: nonEmpty(80),
  advancePercentBp: basisPoints,
  balanceDueDays: z.number().int().min(0).max(365),
  isCredit: z.boolean(),
  active: z.boolean().default(true),
});
export const approvalPolicyRequest = z.object({
  name: nonEmpty(80),
  allowSelfApproval: z.boolean().default(false),
  allowSameApproverMultipleStages: z.boolean().default(false),
  stages: z
    .array(
      z.object({
        name: nonEmpty(80),
        roleCode: z.string().max(40),
        minApprovals: z.number().int().min(1).max(10).default(1),
        appliesWhen: z.enum(['always', 'amount_gte', 'credit_exceeded']).default('always'),
        thresholdMinor: moneyMinor.nullable().optional(),
      }),
    )
    .min(1)
    .max(6),
});
export const supportGrantRequest = z.object({ platformUserEmail: email, reason: nonEmpty(500), hours: z.number().int().min(1).max(72) });

// ---------- Parties ----------
export const partyCategory = z.enum(['pharma_company', 'distributor', 'stockist', 'hospital', 'clinic', 'doctor', 'individual', 'manufacturer', 'third_party_manufacturer', 'supplier', 'logistics_partner', 'other']);
export const addressInput = z.object({
  kind: z.enum(['registered', 'billing', 'delivery', 'branch']),
  label: optionalText(80),
  line1: nonEmpty(200),
  line2: optionalText(200),
  city: nonEmpty(100),
  regionCode: z.string().max(10).nullable().optional(),
  postalCode: z.string().max(20).nullable().optional(),
  countryCode,
  isDefault: z.boolean().default(false),
});
export const contactInput = z.object({
  name: nonEmpty(120),
  designation: optionalText(80),
  email: email.nullable().optional(),
  phone: phone.nullable().optional(),
  isPrimary: z.boolean().default(false),
});
export const createPartyRequest = z
  .object({
    partyCode: z.string().trim().max(30).regex(/^[A-Za-z0-9_-]*$/).optional(),
    isCustomer: z.boolean().default(false),
    isManufacturer: z.boolean().default(false),
    isSupplier: z.boolean().default(false),
    category: partyCategory,
    legalName: nonEmpty(200),
    displayName: nonEmpty(120).optional(),
    countryCode,
    regionCode: z.string().max(10).nullable().optional(),
    email: email.nullable().optional(),
    phone: phone.nullable().optional(),
    website: z.string().max(200).nullable().optional(),
    paymentTermsCode: z.string().max(30).nullable().optional(),
    creditLimitMinor: moneyMinor.nullable().optional(),
    currency: currency.optional(),
    notes: optionalText(2000),
    addresses: z.array(addressInput).max(20).default([]),
    contacts: z.array(contactInput).max(20).default([]),
  })
  .refine((p) => p.isCustomer || p.isManufacturer || p.isSupplier, 'Select at least one relationship (customer, manufacturer or supplier)');
export const updatePartyRequest = z.object({
  category: partyCategory,
  legalName: nonEmpty(200),
  displayName: nonEmpty(120),
  isCustomer: z.boolean(),
  isManufacturer: z.boolean(),
  isSupplier: z.boolean(),
  regionCode: z.string().max(10).nullable().optional(),
  email: email.nullable().optional(),
  phone: phone.nullable().optional(),
  website: z.string().max(200).nullable().optional(),
  paymentTermsCode: z.string().max(30).nullable().optional(),
  creditLimitMinor: moneyMinor.nullable().optional(),
  notes: optionalText(2000),
  version: z.number().int().positive(),
});
export const partyStatusRequest = z.object({ status: z.enum(['active', 'inactive', 'blocked']), reason: optionalText(500) });
export const partyVerificationRequest = z.object({ status: z.enum(['pending', 'verified', 'rejected']), comment: optionalText(500) });
export const partyIdentifierRequest = z.object({ identifierTypeCode: z.string().max(40), value: nonEmpty(80), validFrom: isoDate.nullable().optional(), validTo: isoDate.nullable().optional() });

// ---------- Documents ----------
export const documentMetadata = z.object({
  ownerType: z.enum(['tenant', 'party', 'product', 'sales_order', 'purchase_order']),
  ownerId: z.string().max(64),
  documentTypeCode: z.string().max(60),
  documentNumber: z.string().max(80).optional().nullable(),
  issueDate: isoDate.optional().nullable(),
  expiryDate: isoDate.optional().nullable(),
  notes: optionalText(1000),
});
export const documentReviewRequest = z.object({ decision: z.enum(['verified', 'rejected']), comment: optionalText(1000), version: z.number().int().positive() });

// ---------- Products ----------
export const productRequest = z.object({
  sku: z.string().trim().min(1).max(40).regex(/^[A-Za-z0-9._/-]+$/),
  name: nonEmpty(200),
  description: optionalText(2000),
  category: optionalText(80),
  brand: optionalText(80),
  baseUom: nonEmpty(20),
  packSize: optionalText(80),
  composition: optionalText(1000),
  marketedBy: optionalText(120),
  taxClassificationScheme: z.string().max(20).nullable().optional(),
  taxClassificationCode: z.string().max(20).nullable().optional(),
  taxCategoryId: id.nullable().optional(),
  listPriceMinor: moneyMinor.nullable().optional(),
  salePriceMinor: moneyMinor.nullable().optional(),
  purchasePriceMinor: moneyMinor.nullable().optional(),
  currency: currency.optional(),
  requiresBatch: z.boolean().default(true),
  requiresExpiry: z.boolean().default(true),
  minShelfLifeDays: z.number().int().min(0).max(3650).default(0),
  storageRequirements: optionalText(500),
  qualityRequirements: optionalText(500),
  status: z.enum(['active', 'inactive']).default('active'),
  version: z.number().int().positive().optional(),
});
export const manufacturerProductRequest = z.object({
  manufacturerId: id,
  manufacturerSku: optionalText(60),
  manufacturerProductName: optionalText(200),
  packaging: optionalText(200),
  purchasePriceMinor: moneyMinor.nullable().optional(),
  currency: currency.optional(),
  leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
  minOrderQty: z.number().int().min(1).nullable().optional(),
  isPreferred: z.boolean().default(false),
  active: z.boolean().default(true),
});

// ---------- Sales orders ----------
export const orderLineInput = z.object({
  productId: id,
  quantity,
  unitPriceMinor: moneyMinor.optional(),
  discountBp: basisPoints.default(0),
  requestedDeliveryDate: isoDate.nullable().optional(),
  fulfilmentPreference: z.enum(['auto', 'warehouse', 'direct']).default('auto'),
  notes: optionalText(500),
});
export const createOrderRequest = z.object({
  customerId: id,
  billingAddressId: id.nullable().optional(),
  deliveryAddressId: id.nullable().optional(),
  customerReference: optionalText(80),
  requestedDeliveryDate: isoDate.nullable().optional(),
  paymentTermsCode: z.string().max(30).nullable().optional(),
  notes: optionalText(2000),
  lines: z.array(orderLineInput).min(1).max(200),
});
export const updateOrderRequest = createOrderRequest.extend({ version: z.number().int().positive(), revisionReason: optionalText(500) });
export const orderActionRequest = z.object({ comment: optionalText(1000), version: z.number().int().positive() });
export const approvalDecisionRequest = z.object({ decision: z.enum(['approve', 'reject']), comment: optionalText(1000) });

// ---------- Procurement ----------
export const poLineInput = z.object({
  productId: id,
  salesOrderLineId: id.nullable().optional(),
  quantity,
  unitPriceMinor: moneyMinor.optional(),
  discountBp: basisPoints.default(0),
  taxRateBp: basisPoints.optional(),
  packaging: optionalText(200),
});
export const createPoRequest = z.object({
  manufacturerId: id,
  salesOrderId: id.nullable().optional(),
  deliveryMode: z.enum(['to_warehouse', 'direct_to_customer']).default('to_warehouse'),
  destinationLocationId: id.nullable().optional(),
  attention: optionalText(120),
  expectedDeliveryDate: isoDate.nullable().optional(),
  currency: currency.optional(),
  paymentTermsText: optionalText(300),
  shippingTerms: optionalText(300),
  deliveryInstructions: optionalText(1000),
  packagingRequirements: optionalText(1000),
  batchExpiryRequirements: optionalText(1000),
  qualityRequirements: optionalText(1000),
  termsAndConditions: optionalText(4000),
  notes: optionalText(2000),
  lines: z.array(poLineInput).min(1).max(200),
});
export const updatePoRequest = createPoRequest.extend({ version: z.number().int().positive() });
export const poActionRequest = z.object({ version: z.number().int().positive(), reason: optionalText(500) });
export const poConfirmationRequest = z.object({ lines: z.array(z.object({ poLineId: id, qtyConfirmed: z.number().int().min(0) })).min(1), version: z.number().int().positive() });

// ---------- Inventory ----------
export const locationRequest = z.object({
  code: z.string().regex(/^[A-Z0-9_-]{2,20}$/),
  name: nonEmpty(80),
  kind: z.enum(['warehouse', 'manufacturer_site', 'in_transit', 'quarantine']),
  partyId: id.nullable().optional(),
  address: optionalText(500),
});
export const goodsReceiptRequest = z.object({
  requestKey: z.string().min(8).max(80),
  locationId: id,
  receivedAt: z.string().max(40).optional(),
  supplierReference: optionalText(80),
  notes: optionalText(1000),
  lines: z
    .array(
      z.object({
        poLineId: id,
        batchNo: nonEmpty(60),
        mfgDate: isoDate.nullable().optional(),
        expiryDate: isoDate.nullable().optional(),
        qtyAccepted: z.number().int().min(0),
        qtyRejected: z.number().int().min(0).default(0),
        qualityStatus: z.enum(['available', 'quarantine']).default('available'),
        rejectionReason: optionalText(300),
      }),
    )
    .min(1)
    .max(200),
});
export const stockAdjustmentRequest = z.object({
  requestKey: z.string().min(8).max(80),
  productId: id,
  batchNo: nonEmpty(60),
  expiryDate: isoDate.nullable().optional(),
  mfgDate: isoDate.nullable().optional(),
  locationId: id,
  qualityStatus: z.enum(['available', 'quarantine', 'blocked', 'damaged', 'expired']).default('available'),
  quantityDelta: z.number().int().refine((n) => n !== 0, 'Quantity change cannot be zero'),
  reason: nonEmpty(500),
});
export const qualityChangeRequest = z.object({
  requestKey: z.string().min(8).max(80),
  stockBalanceId: id,
  toStatus: z.enum(['available', 'quarantine', 'blocked', 'damaged', 'expired']),
  quantity,
  reason: nonEmpty(500),
});
export const reserveOrderRequest = z.object({ requestKey: z.string().min(8).max(80) });
export const releaseReservationRequest = z.object({ quantity: quantity.optional(), reason: nonEmpty(300) });

// ---------- Fulfilment ----------
export const shipmentRequest = z
  .object({
    requestKey: z.string().min(8).max(80),
    sourceType: z.enum(['warehouse', 'manufacturer_direct']),
    purchaseOrderId: id.nullable().optional(),
    carrier: optionalText(80),
    trackingNumber: optionalText(80),
    /** Optional logistics details (Logistics module); all can also be set after dispatch. */
    logisticsPartnerId: id.nullable().optional(),
    serviceType: optionalText(60),
    expectedDeliveryDate: isoDate.nullable().optional(),
    notes: optionalText(1000),
    lines: z
      .array(
        z.object({
          orderLineId: id,
          reservationId: id.nullable().optional(),
          poLineId: id.nullable().optional(),
          batchNo: z.string().max(60).nullable().optional(),
          expiryDate: isoDate.nullable().optional(),
          quantity,
        }),
      )
      .min(1)
      .max(200),
  })
  .refine((s) => s.sourceType === 'warehouse' || !!s.purchaseOrderId, 'A direct dispatch needs the manufacturer purchase order');
export const shipmentStatusRequest = z.object({ status: z.enum(['in_transit', 'delivered', 'cancelled']), at: z.string().max(40).optional() });

// ---------- Commercial documents & payments ----------
export const createProformaRequest = z.object({
  salesOrderId: id,
  attention: optionalText(120),
  advanceType: z.enum(['none', 'percent', 'amount']).default('none'),
  advanceValue: z.number().int().min(0).default(0),
  expectedDeliveryDate: isoDate.nullable().optional(),
  termsAndConditions: optionalText(4000),
  artworkApprovalNotes: optionalText(1000),
  notes: optionalText(2000),
  lineIds: z.array(id).max(200).optional(),
});
export const updateProformaRequest = createProformaRequest.omit({ salesOrderId: true, lineIds: true }).extend({ version: z.number().int().positive() });
export const documentActionRequest = z.object({ version: z.number().int().positive(), reason: optionalText(500) });
export const reversalRequest = z.object({ version: z.number().int().positive(), reason: nonEmpty(500) });
export const recordPaymentRequest = z.object({
  requestKey: z.string().min(8).max(80),
  customerId: id,
  amountMinor: moneyMinor.refine((n) => n > 0, 'Amount must be positive'),
  currency,
  method: z.enum(['bank_transfer', 'upi', 'cheque', 'cash', 'card', 'other']),
  reference: optionalText(120),
  paymentDate: isoDate,
  status: z.enum(['expected', 'pending_confirmation']).default('pending_confirmation'),
  notes: optionalText(1000),
});
export const confirmPaymentRequest = z.object({ outcome: z.enum(['received', 'failed']), version: z.number().int().positive(), comment: optionalText(500) });
export const allocatePaymentRequest = z.object({
  allocations: z.array(z.object({ salesOrderId: id, commercialDocumentId: id.nullable().optional(), amountMinor: moneyMinor.refine((n) => n > 0) })).min(1).max(50),
});

// ---------- Notifications ----------
export const notificationTemplateRequest = z.object({
  eventType: z.string().max(60),
  channel: z.enum(['email', 'telegram', 'whatsapp']),
  locale: z.string().max(10).default('en'),
  subject: optionalText(200),
  body: nonEmpty(5000),
  providerTemplateName: optionalText(120),
});
export const notificationPreferenceRequest = z.object({
  preferences: z.array(z.object({ eventType: z.string().max(60), channel: z.enum(['email', 'telegram', 'whatsapp']), enabled: z.boolean() })).max(100),
  scope: z.enum(['tenant', 'me']).default('me'),
});
export const channelConfigRequest = z.object({
  channel: z.enum(['email', 'telegram', 'whatsapp']),
  provider: z.string().max(40),
  enabled: z.boolean(),
  settings: z.record(z.string(), z.string().max(300)).default({}),
  secret: z.string().max(500).optional(),
});
export const consentRequest = z.object({ channel: z.enum(['email', 'telegram', 'whatsapp']), address: nonEmpty(200), status: z.enum(['opted_in', 'opted_out']), source: nonEmpty(200) });
